<script setup>
import { ref, useSlots, nextTick } from 'vue';
import { useWorkspaceV2 } from 'dashboard/composables/useWorkspaceV2';
import { useI18n } from 'vue-i18n';
import { useMediaQuery } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';

import Breadcrumb from 'dashboard/components-next/breadcrumb/Breadcrumb.vue';
import Button from 'dashboard/components-next/button/Button.vue';

defineProps({
  breadcrumbItems: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['back']);

const slots = useSlots();
const { isWorkspaceV2 } = useWorkspaceV2();
const { t } = useI18n();
const isDesktop = useMediaQuery('(min-width: 1024px)');
const panelRef = ref(null);
const toggleRef = ref(null);
const isSidebarOpen = ref(false);

const toggleSidebar = async () => {
  isSidebarOpen.value = !isSidebarOpen.value;
  if (isWorkspaceV2.value && isSidebarOpen.value) {
    await nextTick();
    panelRef.value?.focus();
  }
};

const closeMobileSidebar = () => {
  if (!isSidebarOpen.value) {
    return;
  }

  isSidebarOpen.value = false;
  if (isWorkspaceV2.value) nextTick(() => toggleRef.value?.$el?.focus());
};
const keepPanelFocus = event => {
  if (!isWorkspaceV2.value || event.key !== 'Tab') return;
  const targets = [
    ...panelRef.value.querySelectorAll(
      'button:not([disabled]),a[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex="0"]'
    ),
  ];
  if (!targets.length) {
    event.preventDefault();
    return;
  }
  const first = targets[0];
  const last = targets[targets.length - 1];
  if (
    event.shiftKey &&
    (document.activeElement === first ||
      document.activeElement === panelRef.value)
  ) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
};
</script>

<template>
  <section
    class="flex w-full h-full overflow-hidden justify-evenly bg-n-surface-1"
    :class="{ '!bg-n-background': isWorkspaceV2 }"
  >
    <div
      :inert="(isWorkspaceV2 && isSidebarOpen && !isDesktop) || undefined"
      class="flex min-w-0 flex-col w-full h-full transition-all duration-300 ltr:2xl:ml-56 rtl:2xl:mr-56"
      :class="{ 'ltr:2xl:!ml-0 rtl:2xl:!mr-0': isWorkspaceV2 }"
    >
      <header class="sticky top-0 z-10 px-6 3xl:px-0">
        <div class="w-full mx-auto max-w-[40.625rem]">
          <div
            class="flex flex-col xs:flex-row items-start xs:items-center justify-between w-full py-7 gap-2"
          >
            <Breadcrumb :items="breadcrumbItems" @click="emit('back')" />
          </div>
        </div>
      </header>

      <main class="min-h-0 flex-1 px-6 overflow-y-auto 3xl:px-px">
        <div class="w-full py-4 mx-auto max-w-[40.625rem]">
          <slot />
        </div>
      </main>
    </div>

    <div
      v-if="slots.sidebar && (!isWorkspaceV2 || isDesktop)"
      class="hidden lg:flex flex-col min-w-52 w-full max-w-md border-l border-n-weak bg-n-solid-2"
    >
      <div class="shrink-0">
        <slot name="sidebarHeader" />
      </div>
      <div class="flex-1 overflow-y-auto pb-6 pt-3">
        <slot name="sidebar" />
      </div>
    </div>

    <div
      v-if="slots.sidebar && (!isWorkspaceV2 || !isDesktop)"
      class="lg:hidden fixed top-0 ltr:right-0 rtl:left-0 h-full z-50 flex justify-end transition-all duration-200 ease-in-out"
      :class="isSidebarOpen ? 'w-full' : 'w-16'"
    >
      <div
        v-on-click-outside="[
          closeMobileSidebar,
          { ignore: ['#details-sidebar-content', 'dialog'] },
        ]"
        class="flex items-start p-1 w-fit h-fit relative order-1 xs:top-24 top-28 transition-all bg-n-solid-2 border border-n-weak duration-500 ease-in-out"
        :class="[
          isSidebarOpen
            ? 'justify-end ltr:rounded-l-full rtl:rounded-r-full ltr:rounded-r-none rtl:rounded-l-none'
            : 'justify-center rounded-full ltr:mr-6 rtl:ml-6',
        ]"
      >
        <Button
          ref="toggleRef"
          :aria-label="t('WORKSPACE_V2.COMPANY_DETAIL.SECTIONS')"
          :aria-expanded="isSidebarOpen"
          aria-controls="details-sidebar-content"
          ghost
          slate
          sm
          class="!rounded-full rtl:rotate-180"
          :class="{ 'bg-n-alpha-2': isSidebarOpen }"
          :icon="
            isSidebarOpen
              ? 'i-ri-layout-right-2-line'
              : 'i-ri-layout-right-line'
          "
          data-details-sidebar-toggle
          @click="toggleSidebar"
        />
      </div>

      <Transition
        enter-active-class="transition-transform duration-200 ease-in-out"
        leave-active-class="transition-transform duration-200 ease-in-out"
        enter-from-class="ltr:translate-x-full rtl:-translate-x-full"
        enter-to-class="ltr:translate-x-0 rtl:-translate-x-0"
        leave-from-class="ltr:translate-x-0 rtl:-translate-x-0"
        leave-to-class="ltr:translate-x-full rtl:-translate-x-full"
      >
        <div
          v-if="isSidebarOpen"
          id="details-sidebar-content"
          ref="panelRef"
          :role="isWorkspaceV2 ? 'dialog' : undefined"
          :aria-modal="isWorkspaceV2 || undefined"
          :aria-label="t('WORKSPACE_V2.COMPANY_DETAIL.SECTIONS')"
          tabindex="-1"
          class="order-2 w-[85%] sm:w-[50%] flex flex-col bg-n-solid-2 ltr:border-l rtl:border-r border-n-weak shadow-lg"
          @keydown.esc.stop="closeMobileSidebar"
          @keydown="keepPanelFocus"
        >
          <Button
            v-if="isWorkspaceV2"
            class="self-end m-2"
            :label="t('WORKSPACE_V2.CLOSE')"
            @click="closeMobileSidebar"
          />
          <div class="shrink-0">
            <slot name="sidebarHeader" />
          </div>
          <div class="flex-1 overflow-y-auto pb-6 pt-3">
            <slot name="sidebar" />
          </div>
        </div>
      </Transition>
    </div>
  </section>
</template>
